import styled from 'styled-components'
import { themeGet } from 'styled-system'
import { Box } from 'grommet'
import { HeaderTwo } from 'secondstep-components'
import { getBackgroundColor } from './helpers'
import { SiteSelectorK8WrapperDark } from 'components/SiteSelector'

export const Background = styled(Box).attrs(
  ({
    dataTestId,
    isHighSchoolFamily,
    isMindYetiProduct,
    isSelaProduct,
    isOstProduct,
  }) => {
    const backgroundColor = getBackgroundColor(
      isHighSchoolFamily,
      isSelaProduct,
      isMindYetiProduct,
      isOstProduct,
      themeGet,
    )

    return {
      'data-testid': dataTestId,
      backgroundColor,
    }
  },
)`
  margin-bottom: 1.25rem;
  border-radius: 0.75rem;
  background: ${({ backgroundColor }) => backgroundColor};
  width: 60.875rem;
`

export const Body = styled(Box).attrs(({ isSelaProduct }) => {
  if (!isSelaProduct) {
    return {
      headerColor: themeGet('colors.secondStepBlue'),
      headerFontSize: '1.4375rem',
      headerLineHeight: '1.875rem',
    }
  }
})`
  margin-left: 0.5rem;
  border-radius: 0.625rem;
  background: ${themeGet('colors.white')};

  h2 {
    margin: 0;
    color: ${({ headerColor }) => headerColor};
    font-size: ${({ headerFontSize }) => headerFontSize};
    line-height: ${({ headerLineHeight }) => headerLineHeight};
  }
`

export const Content = styled.div.attrs(({ isHighSchool }) => ({
  fontWeight: isHighSchool ? 700 : 400,
  subheaderFontFamily: isHighSchool
    ? themeGet('font.komet')
    : themeGet('font.molde.semiBold'),
}))`
  padding: 1.25rem 1.25rem 0 1.25rem;

  .program-subheader {
    font-family: ${({ subheaderFontFamily }) => subheaderFontFamily};
    font-weight: ${({ fontWeight }) => fontWeight};
  }

  & > h3 {
    color: ${themeGet('colors.secondStepBlue')};
    margin: 0 0.625rem 0.375rem;
    font-size: 1.125rem;
    line-height: 1.375rem;
    font-family: ${themeGet('font.molde.semiBold')};
  }
`

export const HeaderBox = styled(Box).attrs(
  ({ isHsInstancedView, isMsProduct, isK5Product }) => ({
    flexDirection: isHsInstancedView ? 'column' : 'row',
    isMsProduct,
    isK5Product,
  }),
)`
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection};
  flex-wrap: wrap;
  row-gap: 1.25rem;
  justify-content: space-between;
  margin: 1.75rem 1.25rem 0;
  border-bottom-style: solid;
  // we override these styles for high school:
  border-bottom-width: ${themeGet('dashboard.border.width', '1.5px')};
  border-bottom-color: ${themeGet(
    'dashboard.border.color !important',
    themeGet('colors.paleGray'),
  )};
  align-items: baseline;
  .site-selector {
    display: flex;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .title-button-block {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: start;
    width: 100%;
    row-gap: 0.5rem;
    column-gap: 1.5rem;
    margin-top: ${({ isMsProduct, isK5Product }) =>
      isMsProduct || isK5Product ? '0.75rem' : '0'};
    margin-bottom: 1.125rem;

    a,
    svg {
      color: ${themeGet('colors.buttons.primary.bgActive')};
      border-bottom: 1px solid transparent;
      flex: 0 0 auto;

      &:hover {
        border-bottom: 1px solid transparent;
        color: ${themeGet('colors.buttons.secondary.bgHover')};
      }

      &:focus {
        border-bottom: 1px solid transparent;
        color: ${themeGet('colors.buttons.secondary.bgHover')};
      }
    }

    /* Target StaticLink specifically */
    a[data-testid*='static-link'] {
      &:hover {
        color: ${themeGet('colors.buttons.secondary.bgHover')} !important;
      }

      &:focus {
        color: ${themeGet('colors.buttons.secondary.bgHover')} !important;
      }
    }
  }

  @media (max-width: 1200px) {
    .title-button-block {
      a {
        flex-basis: calc(33.333% - 1rem);
        min-width: 200px;
      }
    }
  }

  @media (max-width: 900px) {
    .title-button-block {
      a {
        flex-basis: calc(50% - 0.75rem);
        min-width: 180px;
      }
    }
  }

  @media (max-width: 700px) {
    .title-button-block {
      a {
        flex-basis: calc(100% - 0.75rem);
        min-width: auto;
      }
    }
  }

  @media (max-width: 960px) {
    .title-button-block {
      flex-direction: column;
      gap: 0.75rem;
      div {
        justify-content: flex-start;
      }
    }
  }

  ${themeGet('breakpoints.mobile')} {
    .site-selector {
      margin-top: 0.5rem;
    }
  }

  @media (max-width: 650px) {
    flex-direction: column;
    row-gap: 1.25rem;

    button {
      align-self: flex-start;
      margin: 0.4375rem 0 0.125rem 0;
    }

    .title-first-block {
      flex-direction: column;
    }
  }
`

export const HighSchoolProgramHeader = styled(HeaderTwo)`
  display: block;
  font-family: ${themeGet('font.komet')};
  font-weight: 800;
  // need to make these !important to override some more-specific styles above:
  font-size: 1.625rem !important;
  color: ${themeGet('colors.highschool.green2')} !important;
  letter-spacing: unset;
  margin-bottom: 0.375rem;
`

export const TileContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  ${themeGet('breakpoints.tabletLarge')} {
    flex-direction: column;
  }
`

export const SiteSelectorWrapperForK8 = styled(SiteSelectorK8WrapperDark)`
  padding: 0;

  div {
    &:hover {
      color: ${themeGet('colors.buttons.secondary.bgHover')} !important;

      svg {
        color: ${themeGet('colors.buttons.secondary.bgHover')} !important;
      }
    }

    &:focus {
      color: ${themeGet('colors.buttons.secondary.bgHover')} !important;

      svg {
        color: ${themeGet('colors.buttons.secondary.bgHover')} !important;
      }
    }
  }
`
